import { ExternalVideo } from '@components/utility/richTexts/extensions';
import type { EditorOptions } from '@tiptap/core';
import { Blockquote } from '@tiptap/extension-blockquote';
import { Bold } from '@tiptap/extension-bold';
import { Code } from '@tiptap/extension-code';
import { Document } from '@tiptap/extension-document';
import { FontFamily } from '@tiptap/extension-font-family';
import { HardBreak } from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import { Highlight } from '@tiptap/extension-highlight';
import { HorizontalRule } from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { BulletList, ListItem, OrderedList, TaskItem, TaskList } from '@tiptap/extension-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Strike } from '@tiptap/extension-strike';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Text } from '@tiptap/extension-text';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { Dropcursor, Gapcursor, UndoRedo } from '@tiptap/extensions';
import { FontSize, HeadingWithAnchor, LinkBubbleMenuHandler, ResizableImage } from 'mui-tiptap';
import { useMemo } from 'react';

const CustomLinkExtension = Link.extend({
    inclusive: false,
});

const CustomSubscript = Subscript.extend({
    excludes: 'superscript',
});

const CustomSuperscript = Superscript.extend({
    excludes: 'subscript',
});

/**
 * A hook for providing a default set of useful extensions for the MUI-Tiptap
 * editor.
 */
export default function useExtensions(): EditorOptions['extensions'] {
    return useMemo(() => {
        return [
            BulletList,
            Document,
            HardBreak,
            ListItem,
            OrderedList,
            Paragraph,
            CustomSubscript,
            CustomSuperscript,
            Text,
            Heading,
            Bold,
            Blockquote,
            Code,
            Italic,
            Underline,
            Strike,
            CustomLinkExtension.configure({
                autolink: true,
                linkOnPaste: true,
                openOnClick: false,
            }),
            LinkBubbleMenuHandler,
            Gapcursor,
            HeadingWithAnchor,
            TextAlign.configure({
                types: ['heading', 'paragraph', 'image', 'externalVideo'],
                defaultAlignment: 'left',
            }),
            TextStyle,
            FontFamily,
            FontSize,
            Highlight.configure({ multicolor: true }),
            HorizontalRule,
            ResizableImage,
            Dropcursor,
            TaskList,
            TaskItem.configure({
                nested: true,
            }),
            UndoRedo,
            Image.configure({
                inline: true,
            }),
            ExternalVideo.configure({
                controls: false,
                nocookie: true,
            }),
        ];
    }, []);
}
